import type { SavedDestination } from "@solomon-apps/shared";
import React, { useRef, useState } from "react";

import { useUpdateSavedDestination } from "@/hooks/useClientSavedDestinations";
import {
    savedDestinationLabel,
    toSavedDestinationInput
} from "@/pages/RewardDestinations/destinationInput";
import { LabelField } from "@/pages/RewardDestinations/LabelField";
import { FormErrorRetry } from "@/pages/shared/FormErrorRetry";
import {
    isClientConnectionError,
    resolveClientFormError
} from "@/utils/clientFormErrors";

/** One width for the inline labels of Destination and Destination Label, so
 *  the two values start on one line ("Destination Label" is the longer). */
export const DESTINATION_PREFIX_CLASS = "w-32";

export const DESTINATION_LABEL_PLACEHOLDER =
    "Enter label for the Destination Address (Optional)";

const NO_OP = (): void => undefined;

/**
 * The "Destination Label" field under the Destination picker, always shown.
 * For a destination just added from the picker's Add row (`editable`) it is
 * enterable and renames it; otherwise it is read-only and shows the picked
 * destination's label (`label`): "Same as Position Source", "Multisig Vault",
 * a saved label, or nothing for an unlabeled one.
 */
export function DestinationLabelField({
    editable,
    label
}: {
    editable: SavedDestination | null;
    label: string;
}): React.ReactElement {
    return (
        <div className="flex flex-col pt-4">
            {editable !== null ? (
                <LabelEditor key={editable.uuid} destination={editable} />
            ) : (
                <LabelField
                    value={label}
                    onChange={NO_OP}
                    disabled
                    placeholder=""
                    prefixLabel="Destination Label"
                    fill
                    prefixClassName={DESTINATION_PREFIX_CLASS}
                />
            )}
        </div>
    );
}

/**
 * Renames the destination through `useUpdateSavedDestination` on blur and on
 * Enter, never per keystroke. An empty label keeps `name === address` (the
 * "no label" convention). A failed rename says why inline, with Retry when the
 * server was unreachable.
 */
function LabelEditor({
    destination
}: {
    destination: SavedDestination;
}): React.ReactElement {
    const updateDestination = useUpdateSavedDestination();
    const [label, setLabel] = useState(() =>
        savedDestinationLabel(destination)
    );
    const [error, setError] = useState<string | null>(null);
    const [retry, setRetry] = useState<(() => void) | null>(null);
    // The name last sent, so a blur after Enter (or an unchanged label)
    // doesn't send the same rename again.
    const sentName = useRef(destination.name);

    const save = async (name: string): Promise<void> => {
        sentName.current = name;
        setError(null);
        setRetry(null);
        try {
            await updateDestination.mutateAsync({
                uuid: destination.uuid,
                data: { name, address: destination.address }
            });
        } catch (e) {
            // Unsent as far as the server is concerned: the next commit of
            // the same label tries again.
            sentName.current = "";
            setError(
                resolveClientFormError(
                    e,
                    "savedDestination",
                    "Couldn't save the label."
                )
            );
            if (isClientConnectionError(e)) {
                setRetry(() => (): void => void save(name));
            }
        }
    };

    const commit = (): void => {
        const { name } = toSavedDestinationInput(destination.address, label);
        if (name === sentName.current) return;
        void save(name);
    };

    return (
        <>
            <LabelField
                value={label}
                onChange={setLabel}
                placeholder={DESTINATION_LABEL_PLACEHOLDER}
                prefixLabel="Destination Label"
                fill
                prefixClassName={DESTINATION_PREFIX_CLASS}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    commit();
                }}
            />
            {error ? (
                <FormErrorRetry
                    className="mt-1.5"
                    error={error}
                    onRetry={retry ?? undefined}
                    busy={updateDestination.isPending}
                />
            ) : null}
        </>
    );
}
